import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  AppBar, 
  Badge,
  Box, 
  Button, 
  Card, 
  CardContent, 
  CardMedia, 
  Chip, 
  Container, 
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider, 
  Drawer,
  Fade,
  Fab,
  IconButton, 
  MenuItem as MuiMenuItem,
  Snackbar,
  Alert,
  TextField,
  Toolbar, 
  Typography, 
  Zoom,
  useScrollTrigger,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  ButtonBase,
} from '@mui/material';
import axios from 'axios';
import { styled } from '@mui/material/styles';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import LunchDiningIcon from '@mui/icons-material/LunchDining';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { criarPedido } from '../services/pedidoService';
import { API_URL, API_KEY } from '../services/pedidoService';
import { getPerfil, salvarPerfil } from '../services/perfilService';
import { validateCoupon, redeemCoupon, fetchActiveCouponBatch, type RedeemCouponResponse } from '../services/couponService';
import { fetchMenuItems } from '../services/menuService';
import { fetchCombos } from '../services/comboService';
import { fetchCompanyByCnpj, type PublicCompany } from '../services/companyService';
import { captureMetricsLead } from '../services/metricsLeadService';
import { useQueryParams } from '../hooks/useQueryParams';
import type { MenuItem } from '../domain/interfaces/cardapio';
import { NEIGHBORHOODS } from '../domain/neighborhoods';
import noImage from '../assets/no-image.png';
import OrdersDialog from '../components/OrdersDialog';

type CartItem = MenuItem & {
  cartItemId: string;
  quantity: number;
  observation?: string;
  comboName?: string;
  comboId?: string;
  isCombo?: boolean;
  numberCombo?: number;
};

const categories = [
  { id: 'todos', label: 'Todos', icon: <LocalDiningIcon /> },
  { id: 'promocao', label: 'Promoções', icon: <LocalOfferIcon /> },
  { id: 'tradicionais', label: 'Tradicionais', icon: <LocalDiningIcon /> },
  { id: 'especiais', label: 'Especiais', icon: <LocalOfferIcon /> },
  { id: 'combos', label: 'Combos', icon: <LocalOfferIcon /> },
  { id: 'xis', label: 'Xis', icon: <LunchDiningIcon /> },
  { id: 'porcoes', label: 'Porções', icon: <RestaurantIcon /> },
  { id: 'bebidas', label: 'Bebidas', icon: <LocalDrinkIcon /> },
];

type PaymentMode = 'DELIVERY' | 'ONLINE';

const BASE_PAYMENT_MODE_OPTIONS = [
  { value: 'DELIVERY', label: 'Pagar na entrega' },
  { value: 'ONLINE', label: 'Pagar agora (Mercado Pago)' },
] as const;

const BASE_PAYMENT_METHODS = [
  { value: 'Dinheiro', label: 'Dinheiro (na entrega)', mode: 'DELIVERY' as PaymentMode },
  { value: 'Cartão de Crédito', label: 'Cartão de Crédito (na entrega)', mode: 'DELIVERY' as PaymentMode },
  { value: 'Cartão de Débito', label: 'Cartão de Débito (na entrega)', mode: 'DELIVERY' as PaymentMode },
  { value: 'PIX', label: 'PIX (na entrega)', mode: 'DELIVERY' as PaymentMode },
  { value: 'ONLINE_WALLET', label: 'Mercado Pago', mode: 'ONLINE' as PaymentMode },
];

const filterPaymentModes = (company: PublicCompany | null) => {
  const onlineEnabled = company?.hasPayments === true;
  return BASE_PAYMENT_MODE_OPTIONS.filter((option) => option.value !== 'ONLINE' || onlineEnabled);
};

const filterPaymentMethods = (company: PublicCompany | null) => {
  const onlineEnabled = company?.hasPayments === true;
  return BASE_PAYMENT_METHODS.filter((method) => method.mode !== 'ONLINE' || onlineEnabled);
};

const methodsByMode = (mode: PaymentMode, company: PublicCompany | null) =>
  filterPaymentMethods(company).filter((method) => method.mode === mode);

const getPaymentModeForMethod = (methodValue: string | undefined, company: PublicCompany | null): PaymentMode => {
  if (!methodValue) {
    return 'DELIVERY';
  }

  const methods = filterPaymentMethods(company);
  const match = methods.find((method) => method.value === methodValue);
  return match?.mode ?? 'DELIVERY';
};

const getDefaultMethodForMode = (mode: PaymentMode, company: PublicCompany | null): string => {
  const methods = filterPaymentMethods(company);
  return methods.find((method) => method.mode === mode)?.value ?? '';
};

const ensurePaymentMethodForMode = (mode: PaymentMode, currentMethod: string, company: PublicCompany | null): string => {
  const methods = filterPaymentMethods(company).filter((method) => method.mode === mode);
  if (!currentMethod) {
    return methods[0]?.value ?? '';
  }

  const match = methods.find((method) => method.value === currentMethod);
  return match ? match.value : methods[0]?.value ?? '';
};

const cities = ['Ballneário Gaivota'];

const neighborhoods = NEIGHBORHOODS;

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  boxShadow: 'none',
  borderBottom: `1px solid ${theme.palette.divider}`,
  transition: 'background-color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'column',
  },
}));

const PriceTag = styled(Typography)({
  display: 'inline-flex',
  alignItems: 'center',
  fontWeight: 'bold',
  color: 'primary.main',
  '& .MuiSvgIcon-root': {
    marginRight: '4px',
    fontSize: '1.2rem',
  },
});

const PERFIL_STORAGE_PREFIX = 'betterdog_perfil_';
const PERFIL_LAST_PHONE_KEY = 'betterdog_last_phone_v1';
const MERCADO_PAGO_PUBLIC_KEY = process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY ?? '';

// CNPJ deve vir da CompanyConfig (multi-tenant)
const getCompanyCnpj = (): string => {
  const cnpjFromEnv = process.env.REACT_APP_COMPANY_CNPJ;
  if (!cnpjFromEnv) {
    throw new Error('REACT_APP_COMPANY_CNPJ não configurado');
  }
  return cnpjFromEnv;
};

const getPerfilStorageKey = (telefoneDigits: string) => `${PERFIL_STORAGE_PREFIX}${telefoneDigits}`;


const tryLoadPerfilFromStorage = (telefoneDigits: string) => {
  try {
    const raw = window.localStorage.getItem(getPerfilStorageKey(telefoneDigits));
    if (!raw) return null;
    return JSON.parse(raw) as any;
  } catch {
    return null;
  }
};

const savePerfilToStorage = (perfil: { telefone: string; nome: string; endereco: Record<string, any>; formaPagamento?: string }) => {
  try {
    const digits = perfil.telefone.replace(/\D/g, '');
    if (!digits) return;
    window.localStorage.setItem(getPerfilStorageKey(digits), JSON.stringify(perfil));
    window.localStorage.setItem(PERFIL_LAST_PHONE_KEY, digits);
  } catch {
  }
};

export default function Menu() {
  const query = useQueryParams<{ channel?: string; source?: string; medium?: string; campaign?: string }>();
  const [activeCategory, setActiveCategory] = useState('todos');
  const [allItems, setAllItems] = useState<MenuItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<MenuItem[]>([]);
  const [isLoadingMenu, setIsLoadingMenu] = useState(true);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLogoDialogOpen, setIsLogoDialogOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>(
    { open: false, message: '', severity: 'success' }
  );
  const [company, setCompany] = useState<PublicCompany | null>(null);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    street: '',
    number: '',
    neighborhood: '',
    city: '',
    reference: '',
    paymentMode: 'DELIVERY' as PaymentMode,
    paymentMethod: getDefaultMethodForMode('DELIVERY', null),
    observations: '',
    couponCode: '',
  });

  const paymentModeOptions = useMemo(() => filterPaymentModes(company), [company]);

  const loadPersistedCheckoutState = () => {
    try {
      const storedCart = window.localStorage.getItem('better_cart');
      if (storedCart) {
        const parsedCart = JSON.parse(storedCart);
        if (Array.isArray(parsedCart)) {
          setCartItems(parsedCart as CartItem[]);
        }
      }

      const storedCustomer = window.localStorage.getItem('better_checkout_info');
      if (storedCustomer) {
        const parsedCustomer = JSON.parse(storedCustomer);
        if (parsedCustomer && typeof parsedCustomer === 'object') {
          setCustomerInfo((prev) => ({
            ...prev,
            ...parsedCustomer,
          }));
        }
      }
    } catch (error) {
      console.error('Erro ao restaurar estado do checkout:', error);
    }
  };

  useEffect(() => {
    loadPersistedCheckoutState();

    try {
      const lastPhone = window.localStorage.getItem(PERFIL_LAST_PHONE_KEY);
      if (!lastPhone) return;

      const storedPerfil = tryLoadPerfilFromStorage(lastPhone);
      if (storedPerfil) {
        setCustomerInfo((prev) => ({
          ...prev,
          name: storedPerfil.nome,
          phone: storedPerfil.telefone,
          street: storedPerfil.endereco?.rua ?? prev.street,
          number: storedPerfil.endereco?.numero ?? prev.number,
          neighborhood: storedPerfil.endereco?.bairro ?? prev.neighborhood,
          city: storedPerfil.endereco?.cidade ?? prev.city,
          reference: storedPerfil.endereco?.complemento ?? prev.reference,
          paymentMode: getPaymentModeForMethod(storedPerfil.formaPagamento, company),
          paymentMethod: ensurePaymentMethodForMode(getPaymentModeForMethod(storedPerfil.formaPagamento, company), storedPerfil.formaPagamento ?? '', company),
        }));
        return;
      }

      setCustomerInfo((prev) => ({ ...prev, phone: lastPhone }));
    } catch {
    }

    try {
      const resumeDataRaw = window.localStorage.getItem('better_checkout_resume');
      if (resumeDataRaw) {
        const resumeData = JSON.parse(resumeDataRaw) as {
          timestamp?: number;
          message?: string;
          resetPaymentMode?: boolean;
          reopenCheckout?: boolean;
        } | null;

        if (resumeData && typeof resumeData === 'object') {
          const isRecent = !resumeData.timestamp || Date.now() - resumeData.timestamp < 10 * 60 * 1000;
          if (isRecent) {
            if (resumeData.resetPaymentMode) {
              setCustomerInfo((prev) => ({
                ...prev,
                paymentMode: 'DELIVERY',
                paymentMethod: getDefaultMethodForMode('DELIVERY', company),
              }));
            }

            if (resumeData.reopenCheckout) {
              setIsCheckoutOpen(true);
            }

            if (resumeData.message) {
              setToast({
                open: true,
                message: resumeData.message,
                severity: 'error',
              });
            }
          }
        }

        window.localStorage.removeItem('better_checkout_resume');
      }
    } catch (resumeError) {
      console.error('Erro ao processar retorno de pagamento:', resumeError);
    }
  }, [company]);

  // Ao montar, se não houver telefone salvo, abrir Dialog de telefone
  const hasPhoneInCache = (() => {
    try {
      const lastPhone = typeof window !== 'undefined'
        ? window.localStorage.getItem(PERFIL_LAST_PHONE_KEY)
        : null;
      return !!(lastPhone && lastPhone.replace(/\D/g, ''));
    } catch {
      return false;
    }
  })();

  // Restaura o estado do acompanhamento de pedidos após reload
  useEffect(() => {
    setIsOrdersDialogOpen(false);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem('better_cart', JSON.stringify(cartItems));
    } catch (error) {
      console.error('Erro ao persistir carrinho:', error);
    }
  }, [cartItems]);

  useEffect(() => {
    try {
      window.localStorage.setItem('better_checkout_info', JSON.stringify(customerInfo));
    } catch (error) {
      console.error('Erro ao persistir dados do checkout:', error);
    }
  }, [customerInfo]);

  // Verificar se há erro de pagamento para exibir toast
  useEffect(() => {
    try {
      const paymentError = localStorage.getItem('better_payment_error');
      if (paymentError) {
        const errorData = JSON.parse(paymentError);
        const now = Date.now();
        
        // Verificar se o erro é recente (menos de 1 minuto)
        if (now - errorData.timestamp < 60000) {
          setToast({
            open: true,
            message: errorData.message,
            severity: 'error'
          });
        }
        
        // Limpar erro do localStorage
        localStorage.removeItem('better_payment_error');
      }
    } catch (error) {
      console.error('Erro ao verificar payment error:', error);
    }
  }, []);

  const openOrdersDialog = () => {
    setIsOrdersDialogOpen(true);
  };

  const handleOpenLogoDialog = () => {
    setIsLogoDialogOpen(true);
  };

  const handleCloseLogoDialog = () => {
    setIsLogoDialogOpen(false);
  };

  const isOpeningSoonDialogOpen = true;
  const [openFood] = useState(false);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [hasCookieConsent, setHasCookieConsent] = useState(false);


  const [isFlavorDialogOpen, setIsFlavorDialogOpen] = useState(false);
  const [selectedDrink, setSelectedDrink] = useState<MenuItem | null>(null);
  const [selectedFlavor, setSelectedFlavor] = useState('');
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [isItemDialogOpen, setIsItemDialogOpen] = useState(false);
  const [itemObservation, setItemObservation] = useState('');
  const [isCouponDialogOpen, setIsCouponDialogOpen] = useState(false);
  const [couponBatchId, setCouponBatchId] = useState<string | null>(null);
  const [couponName, setCouponName] = useState('');
  const [couponPhone, setCouponPhone] = useState('');
  const [couponEmail, setCouponEmail] = useState('');
  const [couponNeighborhood, setCouponNeighborhood] = useState('');
  const [isRedeemingCoupon, setIsRedeemingCoupon] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponSuccessMessage, setCouponSuccessMessage] = useState<string | null>(null);
  const [redeemedCoupon, setRedeemedCoupon] = useState<RedeemCouponResponse | null>(null);

  // Estado para módulo de rastreamento de pedidos (apenas controle de abertura)
  const [isOrdersDialogOpen, setIsOrdersDialogOpen] = useState(false);

  const FRETE_FIXO = 5; // Frete padrão

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const clearOnlinePaymentState = () => {
    setIsProcessingPayment(false);
  };

  const handleCloseCheckout = useCallback(() => {
    clearOnlinePaymentState();
    setIsCheckoutOpen(false);
  }, []);

  const resetCheckoutState = useCallback(() => {
    setCustomerInfo({
      name: '',
      phone: '',
      street: '',
      number: '',
      neighborhood: '',
      city: '',
      reference: '',
      paymentMode: 'DELIVERY' as PaymentMode,
      paymentMethod: getDefaultMethodForMode('DELIVERY', null),
      observations: '',
      couponCode: '',
    });
    setCouponDiscount(0);
    setCartItems([]);
  }, []);

  const calcularFrete = () => {
    if (cartItems.length === 0) {
      return 0;
    }

    if (
      customerInfo.neighborhood === 'Lagoa de Fora'
      || customerInfo.neighborhood === 'Sul Mar'
      || customerInfo.neighborhood === 'Fernão Capelo'
      || customerInfo.neighborhood === 'Onda Azul'
    ) {
      return 10;
    }

    if (
      customerInfo.neighborhood === 'Rua Nova' ||
      customerInfo.neighborhood === 'Figueirinha'
    ) {
      return 13;
    }

    if (
      customerInfo.neighborhood === 'Areias Claras' ||
      customerInfo.neighborhood === 'Sombrio (Centro)'
    ) {
      return 20;
    }

    return FRETE_FIXO;
  };

  const nextCartItemId = useRef(0);

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const lastLeadSignatureRef = useRef<string>('');

  useEffect(() => {
    const channel = query.channel?.trim();
    if (!channel) {
      return;
    }

    const phoneDigits = customerInfo.phone.replace(/\D/g, '');
    const customerName = customerInfo.name.trim();
    const signature = `${channel}|${phoneDigits}|${customerName}`;

    if (lastLeadSignatureRef.current === signature) {
      return;
    }

    const metadata: Record<string, any> = {
      capturedAt: new Date().toISOString(),
    };

    if (typeof window !== 'undefined') {
      metadata.path = window.location.pathname;
      metadata.search = window.location.search;
    }

    lastLeadSignatureRef.current = signature;

    captureMetricsLead({
      cnpj: company?.cnpj ?? getCompanyCnpj(),
      channel,
      source: query.source,
      medium: query.medium,
      campaign: query.campaign,
      phone: phoneDigits || undefined,
      customerName: customerName || undefined,
      metadata,
    }).catch((error) => {
      lastLeadSignatureRef.current = '';

      if (process.env.NODE_ENV !== 'production') {
        console.warn('Falha ao registrar lead', error);
      }
    });
  }, [customerInfo.name, customerInfo.phone, query.campaign, query.channel, query.medium, query.source, company?.cnpj]);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem('betterdog_cookie_consent_v1');
      if (stored === 'accepted') {
        setHasCookieConsent(true);
      }
    } catch {
      // Se não conseguir ler, mantém como falso e o fluxo continua normalmente
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoadingMenu(true);
        const cnpj = getCompanyCnpj();
        const [itemsData, combosData, companyData] = await Promise.all([
          fetchMenuItems(cnpj),
          fetchCombos(cnpj),
          fetchCompanyByCnpj(cnpj),
        ]);
        const merged = [...combosData, ...itemsData];
        setAllItems(merged);
        setFilteredItems(merged);
        setCompany(companyData);

      } catch (error) {
        console.error('Erro ao carregar cardápio ou dados da empresa:', error);
      } finally {
        setIsLoadingMenu(false);
      }
    };

    loadData();
  }, []);

  const handleOpenCouponDialog = async () => {
    setCouponError(null);
    setCouponSuccessMessage(null);
    setRedeemedCoupon(null);
    setCouponName('');
    setCouponPhone('');
    setCouponEmail('');
    setCouponNeighborhood('');
    setIsCouponDialogOpen(true);

    try {
      setIsRedeemingCoupon(true);
      const batch = await fetchActiveCouponBatch(company?.cnpj ?? getCompanyCnpj());
      if (batch) {
        setCouponBatchId(batch.batchId);
      } else {
        setCouponError('Nenhuma campanha ativa de cupons encontrada no momento.');
      }
    } catch (error: any) {
      const rawMessage = (error?.response?.data?.error || error?.message || '').toString();
      let message = error?.response?.data?.error || error?.message || 'Erro ao buscar campanha de cupons.';

      if (rawMessage.includes('No active coupon campaign found')) {
        message = 'Não há campanha de resgate ativa no momento.';
      }

      setCouponError(message);
    } finally {
      setIsRedeemingCoupon(false);
    }
  };

  const handleCloseCouponDialog = () => {
    setIsCouponDialogOpen(false);
  };

  const handleRedeemCouponSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCouponError(null);
    setCouponSuccessMessage(null);
    setRedeemedCoupon(null);

    if (!couponBatchId) {
      setCouponError('Campanha inválida. Entre em contato com a loja.');
      return;
    }

    if (!couponName.trim() || !couponPhone.trim() || !couponEmail.trim() || !couponNeighborhood.trim()) {
      setCouponError('Preencha todos os campos para resgatar seu cupom.');
      return;
    }

    setIsRedeemingCoupon(true);
    try {
      const result = await redeemCoupon(company?.cnpj ?? getCompanyCnpj(), {
        batchId: couponBatchId,
        name: couponName.trim(),
        phone: couponPhone.replace(/\D/g, ''),
        email: couponEmail.trim(),
        bairro: couponNeighborhood.trim(),
      });
      setRedeemedCoupon(result);
      setCouponSuccessMessage('Cupom resgatado com sucesso! Enviamos os dados para o seu e-mail. Você também pode copiar o código abaixo para usar agora.');
    } catch (error: any) {
      const rawMessage = (error?.response?.data?.error || error?.message || '').toString();
      let message = error?.response?.data?.error || error?.message || 'Erro ao resgatar cupom.';

      if (rawMessage.includes('Missing credentials for "PLAIN"') || rawMessage.toLowerCase().includes('smtp')) {
        message = 'Neste momento não foi possível resgatar seu cupom. Tente mais tarde!';
      } else if (rawMessage.includes('Phone already has a coupon for this campaign')) {
        message = 'Número de telefone e e-mail já vinculados a um cupom para essa campanha.';
      }

      setCouponError(message);
    } finally {
      setIsRedeemingCoupon(false);
    }
  };

  useEffect(() => {
    if (activeCategory === 'todos') {
      setFilteredItems(allItems);
    } else {
      setFilteredItems(allItems.filter((item) => item.category === activeCategory));
    }
  }, [activeCategory, allItems]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    });
  };

  const formatComboName = (comboName?: string) => {
    if (!comboName) return '';
    // Remove detalhes após o primeiro " (", ex.: "Combo 8 (Especial)" -> "Combo 8"
    const [base] = comboName.split(' (');
    return base.trim();
  };

  const handleAddToCart = (item: MenuItem, initialObservation?: string) => {
    setCartItems((prev) => {
      const cartItemId = `${item.id}-${nextCartItemId.current++}`;

      // Se for bebida com sabores, usa o primeiro sabor como observação inicial
      const defaultObservation =
        initialObservation ?? (item.category === 'bebidas' && item.flavors && item.flavors.length > 0
          ? item.flavors[0]
          : '');

      const isCombo = item.category === 'combos';
      let numberCombo: number | undefined = undefined;

      if (isCombo) {
        const match = item.name.match(/(\d+)/);
        if (match) {
          const parsed = Number(match[1]);
          if (!Number.isNaN(parsed)) {
            numberCombo = parsed;
          }
        }
      }

      return [
        ...prev,
        {
          ...item,
          cartItemId,
          quantity: 1,
          observation: defaultObservation,
          isCombo,
          comboId: isCombo ? item.id : undefined,
          comboName: isCombo ? item.name : undefined,
          numberCombo,
        } as CartItem,
      ];
    });
    setIsCartOpen(true);
  };

  const handleOpenItemDetails = (item: MenuItem) => {
    setSelectedItem(item);
    setItemObservation('');
    setIsItemDialogOpen(true);
  };

  const handleCloseItemDetails = () => {
    setIsItemDialogOpen(false);
    setSelectedItem(null);
    setItemObservation('');
  };

  const handleAddItemFromDialog = () => {
    if (!selectedItem) return;
    handleAddToCart(selectedItem, itemObservation.trim() || undefined);
    handleCloseItemDetails();
  };

  const handleAddButtonClick = (item: MenuItem) => {
    if (item.category === 'bebidas' && item.flavors && item.flavors.length > 0) {
      setSelectedDrink(item);
      setSelectedFlavor(item.flavors[0] ?? '');
      setIsFlavorDialogOpen(true);
      return;
    }

    handleAddToCart(item);
  };

  const handleDecreaseQuantity = (cartItemId: string) => {
    setCartItems((prev) =>
      prev
        .map((item) =>
          item.cartItemId === cartItemId ? { ...item, quantity: item.quantity - 1 } : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const handleIncreaseQuantity = (cartItemId: string) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.cartItemId === cartItemId ? { ...item, quantity: item.quantity + 1 } : item
      )
    );
  };

  const handleRemoveItem = (cartItemId: string) => {
    setCartItems((prev) => prev.filter((item) => item.cartItemId !== cartItemId));
  };

  const handleItemObservationChange = (cartItemId: string, observation: string) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.cartItemId === cartItemId
          ? { ...item, observation }
          : item
      )
    );
  };

  const totalPrice = cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
  const deliveryFee = calcularFrete();
  const totalWithFrete = totalPrice + deliveryFee;
  const totalWithDiscount = Math.max(0, totalWithFrete - couponDiscount);
  const isOnlinePaymentSelected = customerInfo.paymentMode === 'ONLINE';
  const isWalletPayment = isOnlinePaymentSelected && customerInfo.paymentMethod === 'ONLINE_WALLET';

  const subtotalBruto = cartItems
    .reduce((acc, item) => acc + item.price * item.quantity, 0);

  const descontoCombos = 0;

  const applyPerfilToCustomerInfo = (perfil: any) => {
    setCustomerInfo((prev) => {
      const paymentMethod = perfil.formaPagamento ?? prev.paymentMethod;
      const paymentMode = getPaymentModeForMethod(paymentMethod, company);
      return {
        ...prev,
        name: perfil.nome,
        phone: perfil.telefone,
        street: perfil.endereco?.rua ?? prev.street,
        number: perfil.endereco?.numero ?? prev.number,
        neighborhood: perfil.endereco?.bairro ?? prev.neighborhood,
        city: perfil.endereco?.cidade ?? prev.city,
        reference: perfil.endereco?.complemento ?? prev.reference,
        paymentMode,
        paymentMethod: ensurePaymentMethodForMode(paymentMode, paymentMethod ?? '', company),
      };
    });
  };

  const handleOpenCheckout = async () => {
    clearOnlinePaymentState();
    setIsCheckoutOpen(true);
    // Se o telefone em memória estiver vazio, tentar reaproveitar o telefone salvo em cache
    let phoneDigits = customerInfo.phone.replace(/\D/g, '');
    if (!phoneDigits) {
      try {
        const lastPhone = window.localStorage.getItem(PERFIL_LAST_PHONE_KEY);
        phoneDigits = lastPhone ? lastPhone.replace(/\D/g, '') : '';
        if (phoneDigits) {
          setCustomerInfo((prev) => ({ ...prev, phone: phoneDigits }));
        }
      } catch {
        // se não conseguir ler, segue fluxo normal
      }
    }

    if (!phoneDigits) {
      return;
    }

    const storedPerfil = tryLoadPerfilFromStorage(phoneDigits);
    if (storedPerfil) {
      applyPerfilToCustomerInfo(storedPerfil);
      return;
    }

    try {
      const perfil = await getPerfil(phoneDigits);
      if (perfil) {
        savePerfilToStorage(perfil);
        applyPerfilToCustomerInfo(perfil);
      }
    } catch (error) {
      console.error('Erro ao buscar perfil do cliente:', error);
    }
  };

  const handleCustomerInfoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setCustomerInfo((prev) => {
      if (name === 'paymentMode') {
        const nextMode = value as PaymentMode;
        const nextMethod = ensurePaymentMethodForMode(nextMode, prev.paymentMethod, company);

        if (nextMode !== prev.paymentMode) {
          clearOnlinePaymentState();
        }

        return {
          ...prev,
          paymentMode: nextMode,
          paymentMethod: nextMethod,
        };
      }

      if (name === 'paymentMethod') {
        const nextMode = getPaymentModeForMethod(value, company);
        if (nextMode !== prev.paymentMode || value !== prev.paymentMethod) {
          clearOnlinePaymentState();
        }

        return {
          ...prev,
          paymentMode: nextMode,
          paymentMethod: value,
        };
      }

      return { ...prev, [name]: value };
    });

    if (name === 'phone') {
      const digits = value.replace(/\D/g, '');
      if (digits.length >= 8) {
        const storedPerfil = tryLoadPerfilFromStorage(digits);
        if (storedPerfil) {
          applyPerfilToCustomerInfo(storedPerfil);
          return;
        }

        (async () => {
          try {
            const perfil = await getPerfil(digits);
            if (perfil) {
              savePerfilToStorage(perfil);
              applyPerfilToCustomerInfo(perfil);
            }
          } catch (error) {
            console.error('Erro ao buscar perfil do cliente pelo telefone:', error);
          }
        })();
      }
    }
  };

  const handleAcceptTermsInline = () => {
    try {
      window.localStorage.setItem('betterdog_cookie_consent_v1', 'accepted');
    } catch {
      // ignore
    }
    setHasCookieConsent(true);
  };

  const handleApplyCoupon = async () => {
    const code = customerInfo.couponCode?.trim();
    const phoneDigits = customerInfo.phone.replace(/\D/g, '');

    if (!code) {
      setCouponDiscount(0);
      return;
    }

    if (!phoneDigits) {
      setToast({
        open: true,
        message: 'Informe o telefone antes de aplicar o cupom.',
        severity: 'error',
      });
      return;
    }

    if (cartItems.length === 0) {
      setToast({
        open: true,
        message: 'Adicione itens ao carrinho antes de aplicar o cupom.',
        severity: 'error',
      });
      return;
    }

    try {
      setIsValidatingCoupon(true);
      const orderAmount = totalWithFrete;
      const validationResult = await validateCoupon(company?.cnpj ?? getCompanyCnpj(), {
        code,
        phone: phoneDigits,
        orderAmount,
      });

      setCouponDiscount(validationResult.value);
      setToast({
        open: true,
        message: `Cupom aplicado: desconto de ${formatPrice(validationResult.value)}.`,
        severity: 'success',
      });
    } catch (error: any) {
      console.error('Erro ao validar cupom:', error);
      setCouponDiscount(0);

      let message = 'Não foi possível validar o cupom.';
      if (axios.isAxiosError(error)) {
        const apiMessage = (error.response?.data as any)?.error;
        if (apiMessage) {
          message = apiMessage;
        }
      }

      setToast({
        open: true,
        message,
        severity: 'error',
      });
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleConfirmOrder = async () => {
    // if (!openFood) {
    //   setIsCheckoutOpen(false);
    //   setIsOpeningSoonDialogOpen(true);
    //   return;
    // }

    if (
      !customerInfo.name ||
      !customerInfo.phone ||
      !customerInfo.street ||
      !customerInfo.number ||
      !customerInfo.neighborhood ||
      !customerInfo.city ||
      !customerInfo.paymentMethod ||
      cartItems.length === 0
    ) {
      setToast({
        open: true,
        message:
          'Por favor, preencha todos os campos obrigatórios (dados de entrega e pagamento) e adicione ao menos um item ao carrinho para continuar.',
        severity: 'error',
      });
      return;
    }
    setIsSubmittingOrder(true);

    try {

      const itensPedido = cartItems
        .map((item) => ({
          id: String(item.id),
          nome: item.name,
          quantidade: item.quantity,
          precoUnitario: item.price,
          observacao: item.observation || undefined,
          comboId: item.comboId || undefined,
          comboNome: item.comboName || undefined,
          isCombo: item.isCombo || false,
          numberCombo: item.numberCombo,
        }));

      const valorItens = totalPrice;
      const taxaEntrega = calcularFrete();

      const telefoneDigits = customerInfo.phone.replace(/\D/g, '');

      // Disparar salvamento/atualização de perfil em paralelo ao pedido
      salvarPerfil({
        telefone: telefoneDigits,
        nome: customerInfo.name,
        endereco: {
          rua: customerInfo.street,
          numero: customerInfo.number,
          bairro: customerInfo.neighborhood,
          cidade: customerInfo.city,
          estado: 'SC',
          cep: '88955000',
          complemento: customerInfo.reference,
        },
        formaPagamento: customerInfo.paymentMethod,
      })
        .then((perfilSalvo) => {
          savePerfilToStorage(perfilSalvo);
        })
        .catch((error) => {
          console.error('Erro ao salvar perfil do cliente:', error);
        });

      if (!company?.cnpj) {
        setToast({
          open: true,
          message: 'Erro: CNPJ da empresa não encontrado.',
          severity: 'error',
        });
        setIsSubmittingOrder(false);
        return;
      }

      const order = await criarPedido(company.cnpj, {
        cliente: customerInfo.name,
        telefone: telefoneDigits,
        endereco: {
          rua: customerInfo.street,
          numero: customerInfo.number,
          bairro: customerInfo.neighborhood,
          cidade: customerInfo.city,
          estado: 'SC',
          cep: '88955000',
          complemento: customerInfo.reference,
        },
        itens: itensPedido,
        valorItens,
        taxaEntrega,
        observacao: customerInfo.observations || undefined,
        couponCode: customerInfo.couponCode?.trim() || undefined,
        formaPagamento: customerInfo.paymentMethod,
      });

      // Se for pagamento via Wallet, criar preference e redirecionar
      if (isWalletPayment) {
        if (!company?.cnpj) {
          setToast({
            open: true,
            message: 'Erro: CNPJ da empresa não encontrado. Entre em contato com o suporte.',
            severity: 'error',
          });
          setIsSubmittingOrder(false);
          return;
        }

        try {
          const preferenceResponse = await fetch(
            `${API_URL}/public/orders/${order.id}/payments/mercadopago/preference`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'x-api-key': API_KEY,
                'x-company-cnpj': company.cnpj,
              },
              body: JSON.stringify({
                cnpj: company.cnpj,
              }),
            }
          );

          if (!preferenceResponse.ok) {
            const errorData = await preferenceResponse.json().catch(() => ({}));
            throw new Error(errorData.error || 'Falha ao criar preference de pagamento');
          }

          const { initPoint } = await preferenceResponse.json();
          
          // Redirecionar para o Mercado Pago
          window.location.href = initPoint;
          return;
        } catch (error) {
          console.error('Erro ao criar preference:', error);
          setToast({
            open: true,
            message: 'Erro ao processar pagamento. Tente novamente.',
            severity: 'error',
          });
          setIsSubmittingOrder(false);
          return;
        }
      }



      handleCloseCheckout();
      setIsCartOpen(false);
      resetCheckoutState();

      // Abrir automaticamente o acompanhamento do pedido recém-criado
      setIsOrdersDialogOpen(true);

      setToast({
        open: true,
        message:
          'Pedido enviado com sucesso! Tempo estimado de entrega: 35 a 50 minutos. Obs.: esse tempo pode variar conforme o movimento da casa.',
        severity: 'success',
      });
    } catch (error: any) {
      console.error('Erro ao processar pedido:', error);

      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const message = (error.response?.data as any)?.error;

        if (status === 403 && message === 'Estabelecimento fechado para pedidos no momento') {
          setToast({
            open: true,
            message: 'Estamos fechados para pedidos no momento. Por favor, tente novamente mais tarde.',
            severity: 'error',
          });
          return;
        }
      }

      const fallbackMessage =
        typeof error?.message === 'string'
          ? error.message
          : 'Ocorreu um erro ao processar seu pedido. Por favor, tente novamente.';

      setToast({
        open: true,
        message: fallbackMessage,
        severity: 'error',
      });
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  const isCheckoutValid =
    (openFood
      ? Boolean(customerInfo.name) &&
        Boolean(customerInfo.phone) &&
        Boolean(customerInfo.street) &&
        Boolean(customerInfo.number) &&
        Boolean(customerInfo.neighborhood) &&
        Boolean(customerInfo.city) &&
        Boolean(customerInfo.paymentMethod) &&
        cartItems.length > 0
      : true) && (!isOnlinePaymentSelected || Boolean(MERCADO_PAGO_PUBLIC_KEY));

  const confirmButtonDisabled =
    !hasCookieConsent ||
    !isCheckoutValid ||
    isSubmittingOrder ||
    isProcessingPayment;

  const confirmButtonLabel = isProcessingPayment
    ? 'Processando pagamento...'
    : isSubmittingOrder
    ? 'Confirmando...'
    : 'Confirmar Pedido';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Cabeçalho */}
      <AppBar 
        position="sticky" 
        color="inherit" 
        elevation={trigger ? 4 : 0}
        sx={{
          transition: 'all 0.3s ease',
          backgroundColor: 'background.paper',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        <Toolbar
          sx={{
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1, sm: 0 },
            py: { xs: 1, sm: 0.5 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexGrow: 1,
              gap: 1.5,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <ButtonBase
                onClick={handleOpenLogoDialog}
                sx={{
                  borderRadius: '50%',
                  width: 44,
                  height: 44,
                  p: 0,
                  overflow: 'hidden',
                }}
                aria-label="Ver logo da empresa"
              >
                {company?.logoUrl ? (
                  <Box
                    component="img"
                    src={company.logoUrl}
                    alt={company?.razaoSocial ?? 'Logo da empresa'}
                    sx={{
                      width: 44,
                      height: 44,
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: 18,
                      textTransform: 'uppercase',
                    }}
                  >
                    {company?.razaoSocial?.[0] ?? 'B'}
                  </Box>
                )}
              </ButtonBase>
              <Box sx={{ overflow: 'hidden' }}>
                <Typography variant="h6" component="h1" sx={{ fontWeight: 'bold' }}>
                  Cardápio
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Escolha seu lanche favorito
                </Typography>
              </Box>
            </Box>
            <IconButton
              color="primary"
              onClick={() => setIsCartOpen(true)}
              sx={{
                ml: 1,
              }}
            >
              <Badge
                badgeContent={cartItems.length}
                color="secondary"
              >
                <ShoppingCartIcon sx={{ fontSize: 32 }} />
              </Badge>
            </IconButton>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'center', sm: 'flex-end' },
              width: { xs: '100%', sm: 'auto' },
              mt: { xs: 1, sm: 0 },
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleOpenCouponDialog}
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                px: 2.5,
                flexShrink: 0,
              }}
            >
              Resgatar cupom
            </Button>
            {hasPhoneInCache && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={openOrdersDialog}
                sx={{
                  textTransform: 'none',
                  fontWeight: 'bold',
                  px: 2.5,
                  flexShrink: 0,
                }}
              >
                Status do pedido
              </Button>
            )}
          </Box>
        </Toolbar>
        
        {/* Filtros de categoria */}
        <Box
          sx={{
            px: 2,
            pb: 2,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          {categories.map((category) => (
            <Chip
              key={category.id}
              icon={category.icon}
              label={category.label}
              onClick={() => setActiveCategory(category.id)}
              color={activeCategory === category.id ? 'primary' : 'default'}
              variant={activeCategory === category.id ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </AppBar>

      <Dialog
        open={isLogoDialogOpen}
        onClose={handleCloseLogoDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{company?.razaoSocial ?? 'Logo da empresa'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {company?.logoUrl ? (
            <Box
              component="img"
              src={company.logoUrl}
              alt={company?.razaoSocial ?? 'Logo da empresa'}
              sx={{ width: '100%', maxWidth: 320, borderRadius: 2, objectFit: 'contain' }}
            />
          ) : (
            <Box
              sx={{
                width: 200,
                height: 200,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 72,
                textTransform: 'uppercase',
              }}
            >
              {company?.razaoSocial?.[0] ?? 'B'}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLogoDialog}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Conteúdo principal */}
      <Container maxWidth="lg" sx={{ py: 2, flex: 1 }}>
        {isLoadingMenu ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
            <CircularProgress />
          </Box>
        ) : (
        <Fade in={true} timeout={500}>
          <Box sx={{ width: '100%' }}>
            {company?.message && (
              <Box sx={{ mb: 2 }}>
                <Alert severity="info">{company.message}</Alert>
              </Box>
            )}
            {(['promocao', 'tradicionais', 'especiais', 'xis', 'combos', 'porcoes', 'bebidas'] as const)
              .filter((categoriaId) =>
                activeCategory === 'todos' ? true : activeCategory === categoriaId
              )
              .map((categoriaId) => {
                const sectionItems = filteredItems.filter(
                  (item) => item.category === categoriaId
                );
                if (sectionItems.length === 0) return null;

                const categoriaLabel =
                  categories.find((c) => c.id === categoriaId)?.label ||
                  categoriaId;

                return (
                  <Box key={categoriaId} sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, fontWeight: 'bold' }}
                    >
                      {categoriaLabel}
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: '1fr',
                          md: 'repeat(2, 1fr)',
                          lg: 'repeat(3, 1fr)',
                        },
                        gap: 1.5,
                        width: '100%',
                      }}
                    >
                      {sectionItems.map((item) => (
                        <Box key={item.id} sx={{ width: '100%' }}>
                          <StyledCard
                            onClick={() => handleOpenItemDetails(item)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <Box
                              sx={{
                                position: 'relative',
                                width: { xs: 120, sm: '100%' },
                                height: { xs: '100%', sm: 'auto' },
                                minHeight: { xs: 90, sm: 'auto' },
                                flexShrink: 0,
                                alignSelf: 'stretch',
                              }}
                            >
                              <CardMedia
                                component="div"
                                sx={{
                                  pt: { xs: 0, sm: '60%' },
                                  backgroundColor: 'action.hover',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'text.secondary',
                                  position: 'relative',
                                  width: '100%',
                                  height: '100%',
                                }}
                              >
                                <Box
                                  component="img"
                                  src={item.image || noImage}
                                  alt={item.name}
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                />
                                {item.popular && (
                                  <Chip
                                    label="Popular"
                                    color="secondary"
                                    size="small"
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 8,
                                      fontWeight: 'bold',
                                    }}
                                  />
                                )}
                                {item.newItem && (
                                  <Chip
                                    label="Novo"
                                    color="primary"
                                    size="small"
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      right: item.popular ? 90 : 8,
                                      fontWeight: 'bold',
                                    }}
                                  />
                                )}
                                {item.category === 'promocao' && (
                                  <Chip
                                    label="Promoção"
                                    color="error"
                                    size="small"
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      left: 8,
                                      fontWeight: 'bold',
                                    }}
                                  />
                                )}
                              </CardMedia>
                            </Box>
                            <CardContent
                              sx={{
                                px: 1.25,
                                py: 0.75,
                                flexGrow: 1,
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  mb: 0.25,
                                }}
                              >
                                <Typography
                                  gutterBottom
                                  variant="subtitle1"
                                  component="h3"
                                  sx={{
                                    fontWeight: 'bold',
                                    fontSize: '0.95rem',
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {item.name}
                                </Typography>
                                {item.category === 'combos' && (
                                  <Chip
                                    label="Combo"
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    sx={{
                                      fontSize: '0.65rem',
                                      height: 18,
                                      textTransform: 'uppercase',
                                    }}
                                  />
                                )}
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mt: 'auto',
                                  gap: 1,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <PriceTag
                                  variant="subtitle2"
                                  sx={{
                                    fontSize: '0.9rem',
                                    color:
                                      item.category === 'combos'
                                        ? 'secondary.main'
                                        : 'primary.main',
                                  }}
                                >
                                  <LocalOfferIcon
                                    fontSize="inherit"
                                    sx={{ fontSize: '1rem' }}
                                  />
                                  {formatPrice(item.price)}
                                </PriceTag>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button
                                    variant="text"
                                    color="primary"
                                    size="small"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOpenItemDetails(item);
                                    }}
                                    sx={{
                                      whiteSpace: 'nowrap',
                                      minWidth: 'auto',
                                      px: 0.5,
                                      py: 0.4,
                                      fontSize: '0.72rem',
                                      textDecoration: 'underline',
                                    }}
                                  >
                                    Detalhes
                                  </Button>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleAddButtonClick(item);
                                    }}
                                    sx={{
                                      whiteSpace: 'nowrap',
                                      minWidth: 'auto',
                                      px: 1.1,
                                      py: 0.4,
                                      fontSize: '0.72rem',
                                    }}
                                  >
                                    Adicionar
                                  </Button>
                                </Box>
                              </Box>
                            </CardContent>
                          </StyledCard>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })}
          </Box>
        </Fade>
        )}
      </Container>

      {/* Módulo "Meus pedidos" extraído para componente dedicado */}
      <OrdersDialog
        open={isOrdersDialogOpen}
        onClose={() => {
          setIsOrdersDialogOpen(false);
        }}
      />

      {/* Dialog de resgate de cupom */}
      <Dialog
        open={isCouponDialogOpen}
        onClose={handleCloseCouponDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Resgate seu cupom</DialogTitle>
        <DialogContent dividers>
          {couponError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCouponError(null)}>
              {couponError}
            </Alert>
          )}
          {couponSuccessMessage && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCouponSuccessMessage(null)}>
              {couponSuccessMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={handleRedeemCouponSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nome completo"
              value={couponName}
              onChange={(e) => setCouponName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Telefone (WhatsApp)"
              value={couponPhone}
              onChange={(e) => setCouponPhone(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="E-mail"
              type="email"
              value={couponEmail}
              onChange={(e) => setCouponEmail(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Bairro"
              value={couponNeighborhood}
              onChange={(e) => setCouponNeighborhood(e.target.value)}
              fullWidth
              required
              select
            >
              <MuiMenuItem value="" disabled>
                Selecione...
              </MuiMenuItem>
              {neighborhoods.map((neighborhood) => (
                <MuiMenuItem key={neighborhood} value={neighborhood}>
                  {neighborhood}
                </MuiMenuItem>
              ))}
            </TextField>

            <Box mt={1}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={isRedeemingCoupon}
              >
                {isRedeemingCoupon ? 'Enviando...' : 'Resgatar cupom'}
              </Button>
            </Box>
          </Box>

          {redeemedCoupon && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Cupom resgatado
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Código do cupom: <strong>{redeemedCoupon.code}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Valor de desconto: {formatPrice(redeemedCoupon.value)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pedido mínimo: {formatPrice(redeemedCoupon.minOrderValue)}
              </Typography>
              {redeemedCoupon.expiresAt && (
                <Typography variant="body2" color="text.secondary">
                  Válido até: {new Date(redeemedCoupon.expiresAt).toLocaleString('pt-BR')}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Enviamos os dados do seu cupom para o seu e-mail. Você também pode copiar o código abaixo para usar agora.
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(redeemedCoupon.code);
                      setToast({ open: true, message: 'Código copiado para a área de transferência.', severity: 'success' });
                    } catch {
                      setToast({ open: true, message: 'Não foi possível copiar o código. Copie manualmente.', severity: 'error' });
                    }
                  }}
                >
                  Copiar código
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCouponDialog}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de detalhes do item */}
      <Dialog
        open={isItemDialogOpen && !!selectedItem}
        onClose={handleCloseItemDetails}
        fullWidth
        maxWidth="sm"
      >
        {selectedItem && (
          <>
            <DialogTitle>{selectedItem.name}</DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 2 }}>
                <Box
                  component="img"
                  src={selectedItem.image || noImage}
                  alt={selectedItem.name}
                  sx={{
                    width: '100%',
                    borderRadius: 2,
                    objectFit: 'cover',
                    maxHeight: 260,
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {selectedItem.description}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                {formatPrice(selectedItem.price)}
              </Typography>
              <TextField
                label="Observação do item"
                value={itemObservation}
                onChange={(e) => setItemObservation(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                placeholder="Ex.: Sem cebola, sem maionese"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseItemDetails}>Cancelar</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddItemFromDialog}
              >
                Adicionar ao carrinho
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Botão flutuante do WhatsApp */}
      <Zoom in={!trigger}>
        <Box>
          <Fab
            color="primary"
            aria-label="WhatsApp"
            href="https://wa.me/5548992198889"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              position: 'fixed',
              bottom: '30px',
              right: '30px',
              backgroundColor: '#25D366',
              '&:hover': {
                backgroundColor: '#128C7E',
              },
            }}
          >
            <WhatsAppIcon />
          </Fab>
        </Box>
      </Zoom>

      {/* Botão para voltar ao topo */}
      <Zoom in={trigger}>
        <Box>
          <Fab
            color="primary"
            aria-label="Voltar ao topo"
            onClick={scrollToTop}
            sx={{
              position: 'fixed',
              bottom: '30px',
              right: '30px',
            }}
          >
            <ArrowBackIcon sx={{ transform: 'rotate(-90deg)' }} />
          </Fab>
        </Box>
      </Zoom>

      {/* Drawer do carrinho */}
      <Drawer anchor="right" open={isCartOpen} onClose={() => setIsCartOpen(false)}>
        <Box sx={{ width: { xs: 320, sm: 380 }, p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Typography variant="h6" gutterBottom>
            Meu Carrinho
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {cartItems.length === 0 ? (
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Seu carrinho está vazio. Adicione itens para continuar.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
              {cartItems.map((item) => (
                <Box key={item.cartItemId} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {item.name}
                  </Typography>
                  {item.comboName && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {formatComboName(item.comboName)}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {formatPrice(item.price)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton size="small" onClick={() => handleDecreaseQuantity(item.cartItemId)}>
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="body1" sx={{ minWidth: 24, textAlign: 'center' }}>
                        {item.quantity}
                      </Typography>
                      <IconButton size="small" onClick={() => handleIncreaseQuantity(item.cartItemId)}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {formatPrice(item.price * item.quantity)}
                      </Typography>
                      <Button size="small" color="error" onClick={() => handleRemoveItem(item.cartItemId)}>
                        Remover
                      </Button>
                    </Box>
                  </Box>
                  {item.isCombo ? (
                    item.observation ? (
                      <Typography variant="body2" color="text.secondary">
                        Observação do combo: {item.observation}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        A observação geral do pedido também será considerada para este combo.
                      </Typography>
                    )
                  ) : (
                    <TextField
                      label="Observação deste item"
                      value={item.observation || ''}
                      onChange={(e) => handleItemObservationChange(item.cartItemId, e.target.value)}
                      fullWidth
                      size="small"
                      multiline
                      minRows={1}
                      placeholder="Ex.: Sem cebola"
                      helperText="Ex.: Sem cebola"
                    />
                  )}
                </Box>
              ))}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2">Subtotal</Typography>
            <Typography variant="subtitle2">{formatPrice(subtotalBruto)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="subtitle2">Desconto combos</Typography>
            <Typography variant="subtitle2">{formatPrice(descontoCombos)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="subtitle2">Frete</Typography>
            <Typography variant="subtitle2">{formatPrice(deliveryFee)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Total
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {formatPrice(totalWithFrete)}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setIsCartOpen(false)}
            fullWidth
            sx={{ py: 1, mt: 1, mb: 1 }}
          >
            Continuar comprando
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={cartItems.length === 0}
            onClick={handleOpenCheckout}
            fullWidth
            sx={{ py: 1.2, fontWeight: 'bold' }}
          >
            Finalizar Pedido
          </Button>
        </Box>
      </Drawer>

      {/* Modal de seleção de sabor para bebidas */}
      <Dialog open={isFlavorDialogOpen} onClose={() => setIsFlavorDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Selecione o sabor</DialogTitle>
        <DialogContent dividers>
          <RadioGroup
            value={selectedFlavor}
            onChange={(event) => setSelectedFlavor(event.target.value)}
          >
            {selectedDrink?.flavors?.map((flavor) => (
              <FormControlLabel
                key={flavor}
                value={flavor}
                control={<Radio />}
                label={flavor}
              />
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFlavorDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (selectedDrink && selectedFlavor) {
                handleAddToCart(selectedDrink, selectedFlavor);
              }
              setIsFlavorDialogOpen(false);
            }}
            disabled={!selectedDrink || !selectedFlavor}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de checkout */}
      <Dialog open={isCheckoutOpen} onClose={handleCloseCheckout} fullWidth maxWidth="sm">
        <DialogTitle>Finalizar Pedido</DialogTitle>
        <DialogContent dividers>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nome"
              name="name"
              value={customerInfo.name}
              onChange={handleCustomerInfoChange}
              required
              fullWidth
            />
            <TextField
              label="Telefone"
              name="phone"
              value={customerInfo.phone}
              onChange={handleCustomerInfoChange}
              required
              fullWidth
            />
            <TextField
              label="Rua"
              name="street"
              value={customerInfo.street}
              onChange={handleCustomerInfoChange}
              required
              fullWidth
            />
            <TextField
              label="Número"
              name="number"
              value={customerInfo.number}
              onChange={handleCustomerInfoChange}
              required
              fullWidth
            />
            <TextField
              label="Bairro"
              name="neighborhood"
              value={customerInfo.neighborhood}
              onChange={handleCustomerInfoChange}
              required
              select
              fullWidth
            >
              <MuiMenuItem value="" disabled>
                Selecione...
              </MuiMenuItem>
              {neighborhoods.map((neighborhood) => (
                <MuiMenuItem key={neighborhood} value={neighborhood}>
                  {neighborhood}
                </MuiMenuItem>
              ))}
            </TextField>
            <TextField
              label="Cidade"
              name="city"
              value={customerInfo.city}
              onChange={handleCustomerInfoChange}
              required
              select
              fullWidth
            >
              <MuiMenuItem value="" disabled>
                Selecione...
              </MuiMenuItem>
              {cities.map((city) => (
                <MuiMenuItem key={city} value={city}>
                  {city}
                </MuiMenuItem>
              ))}
            </TextField>
            <TextField
              label="Ponto de referência"
              name="reference"
              value={customerInfo.reference}
              onChange={handleCustomerInfoChange}
              fullWidth
            />
            {/* Modo de Pagamento */}
            {paymentModeOptions.length > 1 && (
              <TextField
                label="Modo de pagamento"
                name="paymentMode"
                value={customerInfo.paymentMode}
                onChange={handleCustomerInfoChange}
                required
                select
                fullWidth
                sx={{ mb: 2 }}
              >
                {paymentModeOptions.map((option) => (
                  <MuiMenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MuiMenuItem>
                ))}
              </TextField>
            )}

            {/* Método de Pagamento - Cards Visuais */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>
                Método de pagamento *
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {methodsByMode(customerInfo.paymentMode, company).map((method) => {
                  const isSelected = customerInfo.paymentMethod === method.value;
                  const isOnlineCard = method.value === 'ONLINE_CARD';
                  const isOnlineWallet = method.value === 'ONLINE_WALLET';
                  const isOnlineMP = isOnlineCard || isOnlineWallet;
                  
                  return (
                    <Box
                      key={method.value}
                      onClick={() => {
                        const currentMode = customerInfo.paymentMode;
                        setCustomerInfo((prev) => ({
                          ...prev,
                          paymentMethod: method.value,
                        }));
                        if (method.mode !== currentMode) {
                          clearOnlinePaymentState();
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        border: 2,
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        cursor: 'pointer',
                        bgcolor: isSelected ? 'primary.light' : 'background.paper',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: isSelected ? 'primary.light' : 'action.hover',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          border: 2,
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          bgcolor: isSelected ? 'primary.main' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {isSelected && (
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: 'white',
                            }}
                          />
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                          {method.label}
                        </Typography>
                        {isOnlineCard && (
                          <Typography variant="caption" color="text.secondary">
                            Pague com seu cartão de forma segura
                          </Typography>
                        )}
                        {isOnlineWallet && (
                          <Typography variant="caption" color="text.secondary">
                            Pix, saldo ou cartão salvo no Mercado Pago
                          </Typography>
                        )}
                      </Box>
                      {isOnlineMP && (
                        <Box
                          component="img"
                          src="https://http2.mlstatic.com/storage/logos-api-admin/a5f047d0-9be0-11ec-aad4-c3381f368aaf-m.svg"
                          alt="Mercado Pago"
                          sx={{ height: 24, opacity: 0.8 }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
            <TextField
              label="Observações"
              name="observations"
              value={customerInfo.observations}
              onChange={handleCustomerInfoChange}
              multiline
              minRows={3}
              fullWidth
            />
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Cupom de desconto
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Código do cupom"
                name="couponCode"
                value={customerInfo.couponCode}
                onChange={handleCustomerInfoChange}
                fullWidth
                placeholder="Se tiver um cupom, digite aqui"
                disabled={couponDiscount > 0}
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={handleApplyCoupon}
                disabled={isValidatingCoupon || !customerInfo.couponCode.trim() || couponDiscount > 0}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {isValidatingCoupon ? 'Validando...' : 'Aplicar'}
              </Button>
            </Box>
            {isWalletPayment && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="info">
                  Ao confirmar, você será redirecionado para o Mercado Pago para concluir o pagamento com sua conta.
                </Alert>
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Itens do pedido
            </Typography>
            <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
              {cartItems.map((item) => (
                <Box key={item.id} sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">
                      {item.quantity}x {item.name}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {formatPrice(item.price * item.quantity)}
                    </Typography>
                  </Box>
                  {item.observation && (
                    <Typography variant="caption" color="text.secondary">
                      Obs.: {item.observation}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2">Subtotal</Typography>
              <Typography variant="subtitle2">{formatPrice(subtotalBruto)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography variant="subtitle2">Frete</Typography>
              <Typography variant="subtitle2">{formatPrice(deliveryFee)}</Typography>
            </Box>
            {couponDiscount > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="subtitle2">
                  Desconto cupom{customerInfo.couponCode ? ` (${customerInfo.couponCode.trim()})` : ''}
                </Typography>
                <Typography variant="subtitle2">- {formatPrice(couponDiscount)}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
              <Typography variant="subtitle1">Total</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {formatPrice(totalWithDiscount)}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'flex-end', gap: 1, px: 3, py: 2 }}>
          {!hasCookieConsent && (
            <Box sx={{ alignSelf: 'stretch', mb: 0.5 }}>
              <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.5 }}>
                Para confirmar o pedido, é necessário aceitar a Política de Privacidade e os Termos de Uso.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleAcceptTermsInline}
                fullWidth
                sx={{ textTransform: 'none', fontWeight: 'bold' }}
              >
                Aceitar termos e continuar
              </Button>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleCloseCheckout}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={handleConfirmOrder}
              disabled={confirmButtonDisabled}
            >
              {confirmButtonLabel}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Aviso de inauguração */}
      <Dialog
        open={isOpeningSoonDialogOpen}
        fullWidth
        maxWidth="xs"
        disableEscapeKeyDown
      >
        <DialogTitle>Comunicado Importante</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 2 }}>
            A Better Dog/Lanches vem, por meio desta publicação, agradecer aos nossos clientes e a todo o povo de
            Balneário Gaivota por essa jornada. Durante todo esse período, fizemos o nosso melhor para atendê-los com
            excelência.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Sempre preparamos nossos produtos com muito amor, mantendo o padrão de qualidade e utilizando ingredientes
            frescos.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Informamos que a Better Dog/Lanches entrará em recesso por tempo indeterminado.
          </Typography>
          <Typography variant="body1">
            Agradecemos imensamente pela parceria e compreensão.
            <br />
            A equipe Better Lanches expressa sua gratidão!
          </Typography>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Rodapé */}
      <Box component="footer" sx={{ bgcolor: 'background.paper', py: 3, mt: 'auto', borderTop: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: { xs: '100%', md: '50%' } }}>
              <Typography variant="body2" color="text.secondary" align="center">
                © {new Date().getFullYear()} Better Lanches - Todos os direitos reservados
              </Typography>
            </Box>
            <Box sx={{ flex: { xs: '100%', md: '50%' }, textAlign: 'center' }}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<WhatsAppIcon />}
                href="https://wa.me/5548992198889"
                target="_blank"
                rel="noopener noreferrer"
              >
                Fale conosco
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
